/**
 * Home Page Desktop ISR — Extracted for code splitting
 *
 * Above-fold components are statically imported.
 * Below-fold components use dynamic() with ssr:true for ISR code-splitting
 * (HTML is still pre-rendered and cached, JS is split into separate chunks).
 */

import React, {useMemo} from 'react';
import dynamic from 'next/dynamic';
import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import 'swiper/swiper-bundle.css';
import {useEntrancePopupController} from '@/_application/pages/home/hooks/use-entrance-popup-controller';
import {DynamicPageServerSideProps} from '@/_application/core/types';
import {Customer} from '@/__generated__/__types__';
import {useSession} from 'next-auth/react';

// Above-fold — static imports (critical for initial render)
import Sidebar from '@/_application/themes/kidsplaza-spring/layouts/components/sidebar/kidsplaza-spring.desktop.sidebar';
import MainBannerSwiper from '@/_application/pages/home/components/main-banner-swiper';
import ShortcutSwiper from '@/_application/pages/home/components/shortcut-swiper';
import SubBannerSwiper from '@/_application/pages/home/components/sub-banner-swiper';
import {FlashSalesSlider} from '@/_application/modules/flash-sales/components/home-page/flash-sales-slider';
import HighlightCategories from '@/_application/pages/home/components/highlight-categories/highlight-categories.desktop';
import PopularBrand from '@/_application/pages/home/components/popular-brand.desktop';
import HomeBannerSwiper from '@/_application/pages/home/components/home-banner-swiper';
import HighLightTabs from '@/_application/modules/products/components/highlight-products-block/highlight-tabs';
import {GuestInfoCard, UserInfoCard} from '@/_application/modules/auth/components';
import {CouponWidgetContent} from '@/nova/modules/sales-rule';

// Below-fold — dynamic imports with ISR (HTML pre-rendered, JS code-split)
const ProductCategorySpotlightList = dynamic(
    () => import('@/_application/pages/home/components/product-category-spotlight/product-category-spotlight-list.desktop'),
    {ssr: true},
);
const ProductsSuggestion = dynamic(
    () => import('@/_application/modules/products/components/products-suggestion/products-suggestion'),
    {ssr: true},
);
const UsefulInformation = dynamic(
    () => import('@/_application/modules/useful-information/useful-info.desktop'),
    {ssr: true},
);
const PopularKeywordsHome = dynamic(
    () => import('@/_application/modules/home/popular-keyword-home'),
    {ssr: true},
);

export default function HomePageDesktopIsr({
    variables,
    customer: _customer,
}: Readonly<{
    variables: DynamicPageServerSideProps['variables'];
    customer?: Customer | null;
}>) {
    const {homeData, categories} = variables;
    const {
        mainBanner,
        subBanner,
        homepageHashtags,
        highlightCategories,
        highlightBrands,
        homepageFeaturedBlocks,
        midHomePageBanners,
        flashSaleData,
        interestedProducts,
        highlightTabsData,
    } = homeData ?? {
        mainBanner: [],
        subBanner: [],
        categories: [],
        highlightCategories: [],
        highlightBrands: null,
        homepageFeaturedBlocks: [],
        midHomePageBanners: [],
        flashSaleData: undefined,
        interestedProducts: [],
        highlightTabsData: undefined,
    };
    const {status} = useSession();
    const {openModal} = useModalAction();
    const isAuthorize = useMemo(() => status === 'authenticated', [status]);

    useEntrancePopupController();

    return (
        <main className="flex flex-col items-center justify-center bg-secondary overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar:horizontal]:hidden">
            <div className="flex min-h-106.5 flex-wrap justify-center gap-4 py-5">
                <Sidebar categories={categories} isHeader={false} />

                <div className="flex w-174.5 flex-col justify-between gap-5">
                    <MainBannerSwiper mainBanner={mainBanner} />
                    <ShortcutSwiper isAuthorize={isAuthorize} onOpenLoginModal={() => openModal('LOGIN_VIEW')} />
                </div>

                {isAuthorize ? <UserInfoCard /> : <GuestInfoCard />}
            </div>

            <div className="w-174.5 lg:w-237.5 xl:w-299">
                <SubBannerSwiper subBanner={subBanner} />
            </div>

            <div className="w-screen">
                <FlashSalesSlider flashSaleData={flashSaleData} />
            </div>

            <div className="mt-5 w-[698px] lg:w-[950px] xl:w-[1204px]">
                <CouponWidgetContent variant="voucher" />
            </div>

            <div className="mt-5 w-[698px] lg:w-[950px] xl:w-[1204px]">
                <CouponWidgetContent variant="eWalletVoucher" />
            </div>

            <div className="my-5 min-h-68 w-174.5 lg:w-237.5 xl:w-301">
                <HighlightCategories highlightCategories={highlightCategories} />
            </div>

            <div className="min-h-49.75">
                <div className="mb-5 w-174.5 lg:w-237.5 xl:w-301">
                    <PopularBrand highlightBrands={highlightBrands} />
                </div>
            </div>

            <div className="min-h-62">
                <div className="mb-5 w-174.5 lg:w-237.5 xl:w-299">
                    <HomeBannerSwiper midHomePageBanners={midHomePageBanners} />
                </div>
            </div>

            <div className="min-h-[56.188rem] w-full bg-[#dff3fe]">
                <div className="mx-auto w-174.5 pb-5 pt-5 lg:w-237.5 xl:w-299">
                    <HighLightTabs highlightTabsData={highlightTabsData} />
                </div>
            </div>

            <div className="min-h-880 w-full">
                <ProductCategorySpotlightList
                    homepageFeaturedBlocks={homepageFeaturedBlocks}
                    highlightBrands={highlightBrands}
                />
            </div>

            <div className="min-h-[54.313rem] w-full bg-[#20C4F433]">
                <div className="mx-auto w-174.5 pb-5 pt-5 lg:w-237.5 xl:w-299">
                    <ProductsSuggestion interestedProducts={interestedProducts} />
                </div>
            </div>

            <div className="min-h-67">
                <UsefulInformation className="w-174.5 pt-5 lg:w-237.5 xl:w-299" />
            </div>

            <PopularKeywordsHome serverHashtags={homepageHashtags} />
        </main>
    );
}


